/**
 * TimelineContainer Component (enhanced in Tasks 5.7, 7.5, 10.7, 2.2, 3.5, 4.2-4.3, track alignment fix, Task Group 1, Task Group 4, and Task Group 5)
 *
 * Main container for the timeline editor with:
 * - Scrollable horizontal/vertical viewport
 * - Grid layout aligning ruler with tracks
 * - Minimum 1 track always visible
 * - Dynamic sizing based on duration and track count
 * - Click empty area to deselect tool (Task 5.7)
 * - Mouse scroll navigation (Task 7.5)
 * - Empty state overlay (not replacement) (Task 4.2-4.3)
 * - Memoized track count calculation to prevent render loops (Task 2.2)
 * - Track management panel integration (Task 3.5)
 * - Synchronized vertical scrolling between track labels and timeline (track alignment fix)
 * - Dynamic zoom via pixelsPerSecond from uiStore (Task Group 1)
 * - Ctrl+scroll wheel zoom support (Task Group 1)
 * - Zoom controls integration (Task Group 1)
 * - Rubber-band selection for multi-select (Task Group 4)
 * - Context menu for group creation and tool operations (Task Group 5)
 *
 * Design specifications:
 * - Track height: 60px (from CSS variable --spacing-timeline-track)
 * - Pixels per second: 50-300 (dynamic, from uiStore)
 * - Minimum tracks visible: 1
 * - Grid layout with track panel on left, ruler at top
 * - Scrollable in both directions
 * - Track labels scroll vertically in sync with timeline tracks
 * - Always shows at least 1 track (from store's initial state)
 */

import { useRef, useEffect, useMemo, useCallback } from 'react';
import { shallow } from 'zustand/shallow';
import {
  useTimelineStore,
  getGroupDuration,
  getGroupToolsAsTools,
} from '../../stores/timelineStore';
import { useUiStore } from '../../stores/uiStore';
import { TimelineRuler } from './TimelineRuler';
import { Track } from './Track';
import { Playhead } from './Playhead';
import { TrackLabelsColumn } from './TrackLabelsColumn';
import { ZoomControls } from './ZoomControls';
import { RubberBandSelection } from './RubberBandSelection';
import { TimelineContextMenu } from './TimelineContextMenu';
import { BreadcrumbBar } from './BreadcrumbBar';
import { Plus } from 'lucide-react';
import { motion } from 'framer-motion';

// Constants
const TRACK_HEIGHT = 60;
const MIN_TRACKS = 1;
const TRACK_PANEL_WIDTH = 150;
const HEADER_HEIGHT = 32; // Matches timeline ruler height
const MAX_TRACKS = 20;
const BREADCRUMB_HEIGHT = 32; // Height for group edit mode breadcrumb bar

export function TimelineContainer() {
  const duration = useTimelineStore((state) => state.duration);
  const tracks = useTimelineStore((state) => state.tracks, shallow);
  const tools = useTimelineStore((state) => state.tools, shallow);

  // Get track management actions from timeline store (Task 3.5)
  const addTrack = useTimelineStore((state) => state.addTrack);
  const removeTrack = useTimelineStore((state) => state.removeTrack);
  const renameTrack = useTimelineStore((state) => state.renameTrack);
  const addGroupTrack = useTimelineStore((state) => state.addGroupTrack);
  const removeGroupTrack = useTimelineStore((state) => state.removeGroupTrack);
  const renameGroup = useTimelineStore((state) => state.renameGroup);

  // Get selection functions from UI store (Task 5.7)
  // NOTE: We use getState() for selectedToolIds instead of subscribing because:
  // 1. It's only used in the keyboard event handler (not for rendering)
  // 2. Subscribing causes the entire TimelineContainer to re-render on selection changes
  // 3. This was a major performance bottleneck
  const deselectToolBlock = useUiStore((state) => state.deselectToolBlock);
  const clearSelection = useUiStore((state) => state.clearSelection);

  // Get tool removal action from timeline store
  const removeTool = useTimelineStore((state) => state.removeTool);

  // Get zoom state and actions from uiStore (Task Group 1)
  const pixelsPerSecond = useUiStore((state) => state.pixelsPerSecond);
  const setPixelsPerSecond = useUiStore((state) => state.setPixelsPerSecond);

  // Group edit mode state (Group Sub-Timeline feature)
  const editingGroupId = useUiStore((state) => state.editingGroupId);
  const exitGroupEditMode = useUiStore((state) => state.exitGroupEditMode);
  const getGroupById = useTimelineStore((state) => state.getGroupById);

  // Get the group being edited (if any)
  const editingGroup = editingGroupId ? getGroupById(editingGroupId) : null;

  /**
   * Edit mode calculations (Group Sub-Timeline feature)
   *
   * When in edit mode, we override:
   * - Duration: use group duration instead of main timeline duration
   * - Tools: convert GroupTools to Tool-like objects for rendering
   * - Track count: based on group's trackCount field
   */
  const editModeTools = useMemo(() => {
    if (!editingGroup) return null;
    return getGroupToolsAsTools(editingGroup);
  }, [editingGroup]);

  const editModeDuration = useMemo(() => {
    if (!editingGroup) return null;
    // Add a small buffer (2 seconds) to the group duration for editing room
    return Math.max(5, getGroupDuration(editingGroup) + 2);
  }, [editingGroup]);

  // Effective values based on edit mode
  const effectiveDuration = editModeDuration ?? duration;

  // Refs for synchronized scrolling
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const trackLabelsRef = useRef<HTMLDivElement>(null);
  const rulerRef = useRef<HTMLDivElement>(null);

  // Ref for the tracks container - used by RubberBandSelection (Task Group 4)
  const tracksContainerRef = useRef<HTMLDivElement>(null);

  // Check if add button should be disabled (group-aware)
  const isAtMaxTracks = editingGroup
    ? (editingGroup.trackCount ?? MIN_TRACKS) >= MAX_TRACKS
    : tracks.length >= MAX_TRACKS;

  // Calculate dimensions using dynamic pixelsPerSecond
  // Use effective duration (group duration in edit mode)
  const timelineWidth = effectiveDuration * pixelsPerSecond;

  /**
   * Memoized visible track count calculation (Task 2.2)
   *
   * In edit mode, uses the group's trackCount field.
   * In normal mode, uses the greater of track list length or max tool track number.
   */
  const visibleTrackCount = useMemo(() => {
    if (editingGroup) {
      return Math.max(MIN_TRACKS, editingGroup.trackCount ?? MIN_TRACKS);
    }
    const maxToolTrack =
      tools.length > 0 ? Math.max(...tools.map((t) => t.trackNumber)) : 0;
    return Math.max(MIN_TRACKS, maxToolTrack, tracks.length);
  }, [tracks.length, tools, editingGroup]);

  const containerHeight = visibleTrackCount * TRACK_HEIGHT;

  // Handle click on empty timeline area (Task 5.7)
  const handleTimelineClick = useCallback(
    (e: React.MouseEvent) => {
      // Only deselect if clicking the timeline background, not a tool block
      const target = e.target as HTMLElement;
      const isTrackDropZone = target.dataset.testid?.includes('drop-zone');
      const isTracksContainer = target.dataset.testid === 'tracks-container';

      if (isTrackDropZone || isTracksContainer) {
        deselectToolBlock();
      }
    },
    [deselectToolBlock]
  );

  /**
   * Synchronize scroll between timeline and its fixed elements:
   * - Vertical scroll syncs with track labels (left panel)
   * - Horizontal scroll syncs with ruler (top)
   */
  const handleTimelineScroll = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    const trackLabels = trackLabelsRef.current;
    const ruler = rulerRef.current;
    if (!scrollContainer) return;

    // Sync vertical scroll with track labels
    if (trackLabels) {
      trackLabels.scrollTop = scrollContainer.scrollTop;
    }

    // Sync horizontal scroll with ruler
    if (ruler) {
      ruler.scrollLeft = scrollContainer.scrollLeft;
    }
  }, []);

  // Handle keyboard shortcuts:
  // - Escape: Exit group edit mode
  // - Delete/Backspace: Delete selected tools
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape key - exit group edit mode
      if (e.key === 'Escape' && editingGroupId) {
        e.preventDefault();
        exitGroupEditMode();
        return;
      }

      // Delete/Backspace - delete selected tools
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Don't delete if focus is in an input field
        const target = e.target as HTMLElement;
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable
        ) {
          return;
        }

        // Get selected tools via getState() to avoid subscription
        const currentSelectedIds = useUiStore.getState().selectedToolIds;

        // Check if there are selected tools
        if (currentSelectedIds.size === 0) return;

        e.preventDefault();

        // Remove each selected tool
        const selectedIds = Array.from(currentSelectedIds);
        selectedIds.forEach((id) => {
          removeTool(id);
        });

        // Clear selection after deletion
        clearSelection();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingGroupId, exitGroupEditMode, removeTool, clearSelection]);

  // Handle mouse wheel scroll navigation (Task 7.5) and Ctrl+scroll zoom (Task Group 1)
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleWheel = (e: WheelEvent) => {
      // Check if Ctrl key is pressed for zoom (Task Group 1)
      if (e.ctrlKey) {
        e.preventDefault();
        // deltaY negative = scroll up = zoom in
        // deltaY positive = scroll down = zoom out
        // Use logarithmic zoom (1.15x per scroll tick) for smooth scaling across wide range
        const currentZoom = useUiStore.getState().pixelsPerSecond;
        const zoomFactor = e.deltaY > 0 ? 1 / 1.15 : 1.15;
        setPixelsPerSecond(currentZoom * zoomFactor);
        return;
      }

      // Check if Shift key is pressed for horizontal scrolling
      if (e.shiftKey) {
        // Horizontal scroll
        e.preventDefault();
        scrollContainer.scrollLeft += e.deltaY;
      }
      // Otherwise, allow default vertical scroll behavior
      // No need to handle it explicitly - browser does it naturally
    };

    // Add wheel event listener with passive: false to allow preventDefault
    scrollContainer.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      scrollContainer.removeEventListener('wheel', handleWheel);
    };
  }, [setPixelsPerSecond]);

  /**
   * Task 4.2-4.3: Always render tracks even when empty
   *
   * Layout structure for synchronized scrolling:
   * - Fixed header row: Add button + Zoom controls (left) + Timeline ruler (right)
   * - Scrollable body row: Track labels (left, synced scroll) + Timeline tracks (right, main scroll)
   *
   * The track labels column has overflow-hidden and its scrollTop is synced
   * via JavaScript to match the timeline's vertical scroll position.
   */
  // Calculate grid template rows based on edit mode
  const gridTemplateRows = editingGroup
    ? `${BREADCRUMB_HEIGHT}px ${HEADER_HEIGHT}px 1fr`
    : `${HEADER_HEIGHT}px 1fr`;

  return (
    <div
      className="relative h-full w-full overflow-hidden"
      style={{
        display: 'grid',
        gridTemplateColumns: `${TRACK_PANEL_WIDTH}px 1fr`,
        gridTemplateRows,
        backgroundColor: 'var(--color-dark-bg)',
      }}
      data-testid="timeline-container"
    >
      {/* Breadcrumb bar for group edit mode - spans both columns */}
      {editingGroup && (
        <div style={{ gridColumn: '1 / -1' }}>
          <BreadcrumbBar
            groupName={editingGroup.name}
            onExitEditMode={exitGroupEditMode}
            onRenameGroup={(newName) => renameGroup(editingGroup.id, newName)}
          />
        </div>
      )}

      {/* Header left: Add Track button and Zoom controls */}
      <div
        className="flex items-center justify-between px-1 gap-1"
        style={{
          backgroundColor: 'var(--color-dark-panel)',
          borderRight: '1px solid var(--color-dark-border)',
          borderBottom: '1px solid var(--color-dark-border)',
        }}
        data-testid="track-panel-header"
      >
        <motion.button
          className="flex items-center justify-center gap-0.5 rounded px-1.5 py-1 text-xs font-medium transition-colors"
          style={{
            backgroundColor: isAtMaxTracks
              ? 'var(--color-dark-bg)'
              : 'var(--color-body-head)',
            color: isAtMaxTracks ? 'var(--color-dark-text-muted)' : 'white',
            cursor: isAtMaxTracks ? 'not-allowed' : 'pointer',
          }}
          onClick={
            isAtMaxTracks
              ? undefined
              : editingGroup
                ? () => addGroupTrack(editingGroup.id)
                : addTrack
          }
          disabled={isAtMaxTracks}
          title={
            isAtMaxTracks ? 'Maximum of 20 tracks reached' : 'Add a new track'
          }
          aria-label={
            isAtMaxTracks ? 'Add Track (maximum reached)' : 'Add Track'
          }
          whileHover={
            isAtMaxTracks
              ? {}
              : {
                  scale: 1.02,
                  backgroundColor: 'var(--color-body-head-hover)',
                }
          }
          whileTap={isAtMaxTracks ? {} : { scale: 0.98 }}
          data-testid="add-track-button"
        >
          <Plus className="h-3 w-3" />
        </motion.button>
        <ZoomControls />
      </div>

      {/* Header right: Timeline ruler (syncs horizontal scroll with tracks) */}
      <div
        ref={rulerRef}
        className="overflow-hidden"
        style={{
          borderBottom: '1px solid var(--color-dark-border)',
        }}
      >
        <div
          style={{
            width: `${timelineWidth}px`,
            minWidth: '100%',
          }}
        >
          <TimelineRuler pixelsPerSecond={pixelsPerSecond} />
        </div>
      </div>

      {/* Body left: Track labels (synced vertical scroll, no horizontal scroll) */}
      <div
        ref={trackLabelsRef}
        className="overflow-hidden"
        style={{
          backgroundColor: 'var(--color-dark-panel)',
          borderRight: '1px solid var(--color-dark-border)',
        }}
        data-testid="track-labels-container"
      >
        <TrackLabelsColumn
          tracks={
            editingGroup
              ? Array.from(
                  { length: editingGroup.trackCount ?? MIN_TRACKS },
                  (_, i) => ({ number: i + 1, label: `Track ${i + 1}` })
                )
              : tracks
          }
          tools={editModeTools ?? tools}
          onDeleteTrack={
            editingGroup ? () => removeGroupTrack(editingGroup.id) : removeTrack
          }
          onRenameTrack={renameTrack}
          canDelete={
            editingGroup
              ? (editingGroup.trackCount ?? MIN_TRACKS) > 1
              : undefined
          }
        />
      </div>

      {/* Body right: Scrollable timeline viewport */}
      <div
        ref={scrollContainerRef}
        className="relative h-full w-full overflow-auto scroll-smooth"
        onScroll={handleTimelineScroll}
        data-testid="timeline-scroll-container"
      >
        {/* Timeline content with fixed dimensions */}
        <div
          className="relative"
          style={{
            width: `${timelineWidth}px`,
            minWidth: '100%',
          }}
        >
          {/* Tracks container - wrapped with context menu (Task Group 5) */}
          <TimelineContextMenu>
            <div
              ref={tracksContainerRef}
              className="relative"
              style={{ height: `${containerHeight}px` }}
              data-testid="tracks-container"
              onClick={handleTimelineClick}
            >
              {Array.from({ length: visibleTrackCount }).map((_, index) => (
                <Track
                  key={index}
                  trackNumber={index + 1}
                  trackIndex={index}
                  duration={effectiveDuration}
                  pixelsPerSecond={pixelsPerSecond}
                  editModeTools={editModeTools ?? undefined}
                  isEditMode={!!editingGroup}
                />
              ))}

              {/* Playhead (hidden in edit mode as it's not relevant) */}
              {!editingGroup && (
                <Playhead
                  pixelsPerSecond={pixelsPerSecond}
                  duration={duration}
                />
              )}

              {/* Rubber-band selection overlay (Task Group 4)
                  Positioned as child of tracks container to capture mouse events
                  and calculate positions relative to tracks area */}
              <RubberBandSelection containerRef={tracksContainerRef} />
            </div>
          </TimelineContextMenu>
        </div>
      </div>
    </div>
  );
}
